import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowUpRight, Camera, Check, Download, ExternalLink, Film, Hash, Image, Link2, ListChecks, Loader, LoaderCircle, Save, Trash2, Upload as UploadIcon, Users, X } from "lucide-react";
import MuxPlayer from "@mux/mux-player-react";
import MuxUploader from "@mux/mux-uploader-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useFeatureFlags, visibleTiers } from "../../lib/featureFlags";
import { useVideoRecord } from "../../lib/useVideoRecord";
import { btnGhost, btnPrimary, fmtDuration, inputCls, labelCls } from "./shared";
import PlatformStatusStrip from "./PlatformStatusStrip";
import ConflictDialog from "./ConflictDialog";
import DescriptionTab from "./DescriptionTab";
import CrewTab from "./CrewTab";
import CtaTab from "./CtaTab";
import ExternalLinksTab from "./ExternalLinksTab";
import LinkedPhotosTab from "./LinkedPhotosTab";
import ChecklistTab from "./ChecklistTab";
import VideoStoreSettings from "../VideoStoreSettings";
import PlatformMatrixOverview from "../PlatformMatrixOverview";
import ScenePhotos from "../../pages/videos/ScenePhotos";
import MediaAssetsTab from "./MediaAssetsTab";
import TeaserSection from "./TeaserSection";
import { newVideoDraft, videoCreatePayload } from "../../lib/videoCreate";

const TABS = [
  ["main", "Main Video", Film], ["description", "Description", Film], ["crew", "Crew", Users],
  ["cta", "CTA / Links", Link2], ["media", "Media Assets", Film],
  ["photos", "Linked Photos", Image], ["platforms", "External Platform Links", ExternalLink],
  ["checklist", "Production Checklist", ListChecks],
];
const STATUS = {
  synced: ["Synced", "text-emerald-300", Check],
  unsaved: ["Unsaved changes", "text-amber-300", AlertTriangle],
  saving: ["Saving…", "text-cyan-300", LoaderCircle],
  conflict: ["Sync conflict", "text-red-300", AlertTriangle],
};

function confirmClose(status, discard, onClose) {
  if (status === "unsaved" && !window.confirm("Discard unsaved changes?")) return;
  if (status === "conflict" && !window.confirm("A sync conflict is unresolved. Close and discard your changes?")) return;
  if (status === "unsaved" || status === "conflict") discard();
  onClose();
}

export default function ManageVideoModal({ videoId, video: initialVideo, createMode = false, source = "catalog", initialTab = "description", onClose, onChange, onCreated }) {
  const [createdVideo, setCreatedVideo] = useState(null);
  const [createDraft, setCreateDraft] = useState(() => ({ ...newVideoDraft(), ...(initialVideo || {}) }));
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState("");
  const createInFlight = useRef(false);
  const id = videoId || initialVideo?.id || createdVideo?.id;
  const creating = createMode && !id;
  const navigate = useNavigate();
  const flags = useFeatureFlags();
  const [activeTab, setActiveTab] = useState(["parts", "extras"].includes(initialTab) ? "media" : initialTab);
  const [highlightPlatform, setHighlightPlatform] = useState(null);
  const [crm, setCrm] = useState([]);
  const [crmLoading, setCrmLoading] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const [masterBusy, setMasterBusy] = useState(false);
  const [technicalDetails, setTechnicalDetails] = useState(false);
  const [muxMode, setMuxMode] = useState("upload");
  const [uploadInfo, setUploadInfo] = useState(null);
  const [manualPlaybackId, setManualPlaybackId] = useState("");
  const [libraryAssets, setLibraryAssets] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [grabbedFrames, setGrabbedFrames] = useState([]);
  const [extractingThumbnails, setExtractingThumbnails] = useState(false);
  const [showThumbnailPicker, setShowThumbnailPicker] = useState(false);
  const [autoThumbnails, setAutoThumbnails] = useState([]);
  const [trailerUploadInfo, setTrailerUploadInfo] = useState(null);
  const [muxPolling, setMuxPolling] = useState(false);
  const [downloadingMp4, setDownloadingMp4] = useState(false);
  const playerRef = useRef(null);
  const persistedSync = useVideoRecord(id, source, { autosave: Boolean(id) });
  const setDraftField = (field, value) => {
    setCreateDraft((current) => ({ ...current, [field]: value }));
    setCreateError("");
  };
  const createVideo = async () => {
    if (createInFlight.current) return false;
    const payload = videoCreatePayload(createDraft);
    if (!payload.title) { setCreateError("A video title is required."); return false; }
    createInFlight.current = true;
    setCreateBusy(true);
    setCreateError("");
    try {
      const response = await api.post("/api/videos/", payload, { _noOfflineQueue: true });
      if (response.status === 202 || response.data?.pending || !response.data?.id) {
        setCreateError("The video has not been created. Your draft is still here; reconnect and try again.");
        return false;
      }
      const saved = response.data;
      setCreatedVideo(saved);
      onCreated?.(saved);
      onChange?.(saved);
      toast.success("Video created. The full editor is now ready.");
      return true;
    } catch (error) {
      const detail = error?.response?.data?.detail;
      setCreateError(typeof detail === "string" ? detail : "Could not create the video. Your draft is kept; please try again.");
      return false;
    } finally {
      createInFlight.current = false;
      setCreateBusy(false);
    }
  };
  const sync = creating ? {
    record: createDraft, serverRecord: null, loading: false, loadError: null,
    status: createBusy ? "saving" : "unsaved", dirty: true, saving: createBusy,
    draft: createDraft, conflicts: null,
    setField: setDraftField,
    setPlatform: (key, patch) => setCreateDraft((current) => ({ ...current, platform_status: {
      ...current.platform_status, [key]: { ...current.platform_status?.[key], ...patch },
    } })),
    save: createVideo, discard: () => setCreateDraft(newVideoDraft()),
  } : persistedSync;
  const record = sync.record || createdVideo || initialVideo;

  useEffect(() => {
    let active = true;
    api.get("/api/crm/?limit=1000").then((r) => { if (active) setCrm(Array.isArray(r.data) ? r.data : []); })
      .catch(() => toast.error("Could not load CRM people"))
      .finally(() => { if (active) setCrmLoading(false); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (id && sync.serverRecord && onChange) onChange(sync.serverRecord);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sync.serverRecord]);

  const tiers = useMemo(() => visibleTiers(flags), [flags]);
  const selectedTiers = record?.tiers || [];
  const currentStatus = STATUS[sync.status] || STATUS.synced;
  const StatusIcon = currentStatus[2];
  const activeVideo = record || {};
  const setField = sync.setField;
  const setPlatform = (key, patch) => {
    sync.setPlatform(key, patch);
    if (creating) return;
    onChange?.({
      id,
      platform_status: {
        ...(activeVideo.platform_status || {}),
        [key]: { ...(activeVideo.platform_status?.[key] || {}), ...patch },
      },
    });
  };
  const handlePlatformUpdate = (updated) => {
    onChange?.(updated);
    sync.reload();
  };

  const selectPlatform = (key) => {
    setHighlightPlatform(key);
    setActiveTab("platforms");
  };

  const retryPlayback = async () => {
    setMasterBusy(true);
    try {
      const r = await api.get(`/api/mux/status/${id}`);
      onChange?.({ id, ...r.data });
      await sync.reload();
      if (r.data?.mux_playback_id || r.data?.mux_status === "ready") toast.success("Playback status refreshed");
      else toast.error("Playback is still unavailable");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not refresh playback status");
    } finally { setMasterBusy(false); }
  };

  const pollTrailerStatus = async () => {
    try {
      const r = await api.get(`/api/mux/trailer/status/${id}`);
      const trailer = r.data || {};
      onChange?.({
        id,
        trailer_mux_upload_id: trailer.trailer_mux_upload_id,
        trailer_mux_asset_id: trailer.trailer_mux_asset_id,
        trailer_mux_playback_id: trailer.trailer_mux_playback_id,
        trailer_mux_status: trailer.trailer_mux_status,
        trailer_mux_duration: trailer.trailer_mux_duration,
      });
      await sync.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not refresh trailer status");
    }
  };

  const removeTrailer = async () => {
    if (!window.confirm("Remove the trailer?")) return;
    try {
      await api.delete(`/api/mux/trailer/${id}`);
      onChange?.({
        id,
        trailer_mux_upload_id: null,
        trailer_mux_asset_id: null,
        trailer_mux_playback_id: null,
        trailer_mux_status: null,
        trailer_mux_duration: null,
      });
      setTrailerUploadInfo(null);
      toast.success("Trailer removed");
      await sync.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove trailer");
    }
  };

  const doClose = () => { if (!createInFlight.current) confirmClose(sync.status, sync.discard, onClose); };
  const saveAndClose = async () => { if (await sync.save()) onClose(); };
  const saveChanges = async () => { await sync.save(); };
  const statusRows = record ? record : initialVideo || {};

  const loadAssets = async () => {
    setLibraryLoading(true);
    try {
      const r = await api.get("/api/mux/assets?limit=50");
      setLibraryAssets(r.data.assets || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load Mux library"); }
    finally { setLibraryLoading(false); }
  };

  const linkAsset = async (playbackId) => {
    if (!playbackId) return;
    try {
      const r = await api.post("/api/mux/link", { video_id: id, playback_id: playbackId, asset_id: playbackId });
      onChange?.({ id, mux_playback_id: r.data.playback_id, mux_asset_id: r.data.asset_id, mux_status: "ready", mux_duration: r.data.duration });
      setMuxMode("preview");
      toast.success("Mux asset linked");
      await sync.reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Link failed"); }
  };

  const startUpload = async () => {
    try {
      const r = await api.post("/api/mux/upload", { video_id: String(id) });
      setUploadInfo({ ...r.data, url: r.data.upload_url || r.data.url });
      onChange?.({ id, mux_upload_id: r.data.upload_id, mux_status: "awaiting_upload" });
      setMuxMode("uploading");
      toast.success("Upload started");
      await sync.reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Mux upload create failed"); }
  };

  const pollMuxStatus = async () => {
    setMuxPolling(true);
    try {
      const r = await api.get(`/api/mux/status/${id}`);
      onChange?.({ id, ...r.data });
      await sync.reload();
      if (r.data?.mux_playback_id || r.data?.mux_status === "ready") toast.success("Playback status refreshed");
      else toast.info("Mux is still processing this upload");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not refresh playback status");
    } finally { setMuxPolling(false); }
  };

  const captureFrame = async () => {
    const t = Math.max(0, playerRef.current?.currentTime ?? 0);
    if (!safeRecord.mux_playback_id) return;
    try {
      const r = await api.get(`/api/mux/frame-url/${id}`, { params: { time: t, width: 1280 } });
      setGrabbedFrames((prev) => [...prev, { time: t, url: r.data.url }]);
      toast.success("Frame captured");
    } catch (e) { toast.error(e?.response?.data?.detail || "Capture failed"); }
  };

  const downloadMp4 = async () => {
    setDownloadingMp4(true);
    try {
      const r = await api.post(`/api/mux/enable-mp4/${id}`);
      if (r.data?.download_url) window.open(r.data.download_url, "_blank");
      else toast.info("Download is still processing");
    } catch (e) { toast.error(e?.response?.data?.detail || "Download failed"); }
    finally { setDownloadingMp4(false); }
  };

  const extractThumbnails = async () => {
    if (extractingThumbnails) return;
    setExtractingThumbnails(true);
    try {
      const r = await api.post(`/api/mux/extract-thumbnails/${id}`);
      const thumbs = r.data.thumbnails || [];
      setAutoThumbnails(thumbs);
      onChange?.({ id, auto_thumbnails: thumbs });
      await sync.reload();
      setShowThumbnailPicker(true);
      toast.success(`Extracted ${thumbs.length} thumbnails. Click one to set it as the cover.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Thumbnail extraction failed"); }
    finally { setExtractingThumbnails(false); }
  };

  const selectThumbnail = async (url) => {
    try {
      await api.post(`/api/mux/set-thumbnail/${id}`, { thumbnail_url: url });
      onChange?.({ id, cover_image: url });
      await sync.reload();
      setShowThumbnailPicker(false);
      toast.success("Thumbnail set as cover image");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to set thumbnail"); }
  };

  const startTrailerUpload = async () => {
    try {
      const r = await api.post("/api/mux/trailer/upload", { video_id: id });
      setTrailerUploadInfo({ ...r.data, url: r.data.upload_url || r.data.url });
      onChange?.({ id, trailer_mux_upload_id: r.data.upload_id, trailer_mux_status: "awaiting_upload" });
      await sync.reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Trailer upload create failed"); }
  };

  const removeAsset = async () => {
    if (!window.confirm("Remove the master Mux video?")) return;
    try {
      await api.delete(`/api/mux/${id}`);
      onChange?.({ id, mux_upload_id: null, mux_asset_id: null, mux_playback_id: null, mux_status: null, mux_duration: null });
      setMuxMode("upload");
      toast.success("Mux asset removed");
      await sync.reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  if (!id && !creating) return null;
  if (sync.loading && !record) {
    return <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80"><div className="rounded-xl border border-zinc-800 bg-zinc-950 p-8 text-sm text-zinc-300"><LoaderCircle size={18} className="inline mr-2 animate-spin" />Loading video record…</div></div>;
  }
  if (sync.loadError && !record) {
    return <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-4"><div className="w-full max-w-md rounded-xl border border-red-900 bg-zinc-950 p-6"><p className="text-red-300">{sync.loadError}</p><div className="flex gap-2 mt-4"><button className={btnGhost} onClick={onClose}>Close</button><button className={btnPrimary} onClick={sync.reload}>Retry</button></div></div></div>;
  }

  const safeRecord = {
    ...activeVideo,
    cast_crm_ids: activeVideo.cast_crm_ids || initialVideo?.cast_crm_ids || [],
    cameraman_crm_ids: activeVideo.cameraman_crm_ids || [],
    producer_crm_ids: activeVideo.producer_crm_ids || [],
    tags: activeVideo.tags || [],
    platform_status: activeVideo.platform_status || {},
    scene_photos: activeVideo.scene_photos || [],
    extra_videos: activeVideo.extra_videos || [],
    visibility: activeVideo.visibility || [],
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/85 p-0 sm:p-4" onMouseDown={doClose} data-testid="manage-video-modal">
      <div role="dialog" aria-modal="true" aria-label={creating ? "Add video · Manage Video" : `Manage ${safeRecord.title || "video"}`} onMouseDown={(e) => e.stopPropagation()}
        className="w-full max-w-6xl max-h-[96vh] sm:max-h-[92vh] flex flex-col overflow-hidden rounded-t-2xl sm:rounded-2xl border border-zinc-800 bg-[#09090b] shadow-2xl">
        <header className="shrink-0 border-b border-zinc-800 bg-zinc-950/95">
          <div className="flex items-start justify-between gap-4 px-4 sm:px-6 pt-4 pb-3">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <input className="min-w-[12rem] flex-1 bg-transparent text-lg font-bold text-white outline-none focus:underline decoration-purple-400 underline-offset-4"
                  aria-label="Video title" placeholder={creating ? "New video title" : "Untitled video"} value={safeRecord.title || ""} onChange={(e) => setField("title", e.target.value)} />
                <span className={`inline-flex items-center gap-1.5 text-xs ${currentStatus[1]}`} data-testid="video-sync-status">
                  <StatusIcon size={13} className={sync.status === "saving" ? "animate-spin" : ""} /> {currentStatus[0]}
                </span>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className={labelCls + " !mb-0 mr-1"}>Member tier</span>
                {tiers.map((tier) => {
                  const selected = selectedTiers.includes(tier.id);
                  return <button type="button" key={tier.id} aria-pressed={selected}
                    onClick={() => setField("tiers", selected ? selectedTiers.filter((x) => x !== tier.id) : [...selectedTiers, tier.id])}
                    className={`rounded-full border px-3 py-1 text-xs font-semibold ${selected ? "border-cyan-400 bg-cyan-900/30 text-cyan-200" : "border-zinc-700 text-zinc-400 hover:text-white"}`}>{tier.label} {selected ? "✓" : ""}</button>;
                })}
                {!flags.ENABLE_PAID_TIERS && activeVideo.hidden_tiers?.length > 0 && <span className="text-[10px] text-zinc-600">Paid assignments hidden and preserved</span>}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <div className="hidden md:flex gap-1.5" hidden={creating}>
                <button type="button" disabled={creating} className={btnGhost + " !px-2.5 !py-1.5 !text-xs"} onClick={() => navigate(`/crm/catalog?videoId=${encodeURIComponent(id)}`)}><ArrowUpRight size={13} className="inline mr-1" />Open in Catalog</button>
                <button type="button" disabled={creating} className={btnGhost + " !px-2.5 !py-1.5 !text-xs"} onClick={() => navigate(`/crm/member-areas?videoId=${encodeURIComponent(id)}`)}><ArrowUpRight size={13} className="inline mr-1" />Open in Member Areas</button>
              </div>
              <button type="button" onClick={doClose} disabled={createBusy} aria-label="Close Manage Video" className="p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800"><X size={18} /></button>
            </div>
          </div>
          <div className="px-4 sm:px-6 pb-3">
            <PlatformStatusStrip video={statusRows} onSelect={selectPlatform} />
          </div>
          <nav className="flex gap-1 overflow-x-auto px-3 sm:px-5 border-t border-zinc-800" aria-label="Video management tabs">
            {TABS.map(([key, label, Icon]) => (
              <button type="button" key={key} role="tab" aria-selected={activeTab === key} onClick={() => setActiveTab(key)}
                className={`shrink-0 flex items-center gap-1.5 px-3 py-3 border-b-2 text-xs sm:text-sm transition ${activeTab === key ? "border-purple-400 text-white" : "border-transparent text-zinc-500 hover:text-zinc-200"}`}>
                <Icon size={14} />{label}
              </button>
            ))}
          </nav>
        </header>

        <main className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6">
          {creating && <p className="mb-4 rounded-lg border border-cyan-500/30 bg-cyan-950/30 p-3 text-sm text-cyan-100">New local draft. Create the video to enable uploads, cuts, linked media and other ID-based actions. Nothing is saved automatically before creation.</p>}
          {createError && <p role="alert" className="mb-4 rounded-lg border border-red-500/40 bg-red-950/20 p-3 text-sm text-red-200">{createError}</p>}
          {activeTab === "main" && (
            <fieldset disabled={creating} className="min-w-0 space-y-5 disabled:opacity-60">
              <section className="grid gap-4 rounded-xl border border-zinc-800 bg-black/40 p-4 sm:grid-cols-2" aria-label="Video details">
                <label className="block text-sm text-zinc-300" htmlFor="mv-location">
                  Location / Venue
                  <input id="mv-location" className={`${inputCls} mt-1`} value={safeRecord.location || ""}
                    onChange={(event) => setField("location", event.target.value)} placeholder="Studio, city, or venue" />
                </label>
                <label className="block text-sm text-zinc-300" htmlFor="mv-runtime-minutes">
                  Duration override (minutes)
                  <input id="mv-runtime-minutes" type="number" min="0" step="0.1" className={`${inputCls} mt-1`}
                    value={safeRecord.runtime_minutes ?? ""}
                    onChange={(event) => setField("runtime_minutes", event.target.value === "" ? null : Number(event.target.value))}
                    placeholder={safeRecord.mux_duration ? String(Math.round(Number(safeRecord.mux_duration) / 60)) : "Use detected duration"} />
                  <span className="mt-1 block text-xs text-zinc-500">
                    {safeRecord.mux_duration ? `Mux detected ${fmtDuration(safeRecord.mux_duration)}` : "Mux duration has not been detected yet."}
                  </span>
                </label>
              </section>
              <div className="rounded-xl border border-zinc-800 bg-black/40 p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-cyan-400">Main Video</h3>
                  {safeRecord.mux_playback_id && <button type="button" className={btnGhost} onClick={removeAsset}><Trash2 size={14} className="inline mr-1" />Delete</button>}
                </div>
                {!safeRecord.mux_playback_id ? (
                  <div className="space-y-3">
                    {safeRecord.mux_upload_id && <div className="flex items-center justify-between rounded-lg border border-amber-500/30 bg-amber-900/10 p-3 text-sm text-amber-200"><span>Upload status: {safeRecord.mux_status || "processing"}</span><button type="button" onClick={pollMuxStatus} disabled={muxPolling} className={btnGhost}>{muxPolling ? "Checking..." : "Refresh playback status"}</button></div>}
                    <div className="grid gap-3 md:grid-cols-3">
                      <button type="button" onClick={startUpload} className="rounded-xl border border-cyan-500/30 bg-cyan-900/20 px-4 py-4 text-left hover:border-cyan-500"><UploadIcon size={22} className="mb-2 text-cyan-300" /><div className="font-semibold text-white">Upload New</div><div className="text-xs text-zinc-400">Direct upload to Mux</div></button>
                      <button type="button" onClick={() => setMuxMode("manual")} className="rounded-xl border border-purple-500/30 bg-purple-900/20 px-4 py-4 text-left hover:border-purple-500"><Hash size={22} className="mb-2 text-purple-300" /><div className="font-semibold text-white">Enter Mux ID</div><div className="text-xs text-zinc-400">Link existing asset</div></button>
                      <button type="button" onClick={() => { setMuxMode("library"); loadAssets(); }} className="rounded-xl border border-pink-500/30 bg-pink-900/20 px-4 py-4 text-left hover:border-pink-500"><Film size={22} className="mb-2 text-pink-300" /><div className="font-semibold text-white">From Library</div><div className="text-xs text-zinc-400">Choose from Mux</div></button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <MuxPlayer ref={playerRef} playbackId={safeRecord.mux_playback_id} streamType="on-demand" className="w-full aspect-video" accent-color="#22d3ee" />
                    <div className="flex flex-wrap gap-2">
                      <button type="button" onClick={captureFrame} className={btnGhost}><Camera size={14} className="inline mr-1" />Capture frame</button>
                      <button type="button" onClick={extractThumbnails} disabled={extractingThumbnails} className={`${btnGhost} disabled:opacity-60`}>{extractingThumbnails ? <LoaderCircle size={14} className="inline mr-1 animate-spin" /> : <Image size={14} className="inline mr-1" />}{extractingThumbnails ? "Extracting..." : "Extract thumbnails"}</button>
                      <button type="button" onClick={downloadMp4} disabled={downloadingMp4} className={btnGhost}><Download size={14} className="inline mr-1" />{downloadingMp4 ? "Preparing MP4..." : "Download MP4"}</button>
                      <button type="button" onClick={pollMuxStatus} disabled={muxPolling} className={btnGhost}>{muxPolling ? "Checking..." : "Refresh playback status"}</button>
                    </div>
                    {showThumbnailPicker && autoThumbnails.length > 0 && <div className="grid gap-2 md:grid-cols-5">{autoThumbnails.map((thumb, idx) => <button type="button" key={idx} onClick={() => selectThumbnail(thumb.url || thumb)} className="overflow-hidden rounded-lg border border-zinc-700"><img src={thumb.url || thumb} alt="thumb" className="h-20 w-full object-cover" /></button>)}</div>}
                    {grabbedFrames.length > 0 && <div className="grid gap-2 md:grid-cols-4">{grabbedFrames.map((frame, idx) => <img key={idx} src={frame.url} alt="Frame capture" className="h-20 w-full object-cover rounded-lg border border-zinc-700" />)}</div>}
                  </div>
                )}
                {uploadInfo && !safeRecord.mux_playback_id && <div className="mt-4 rounded-xl border border-dashed border-cyan-500/40 bg-zinc-950/60 p-4"><MuxUploader endpoint={uploadInfo.url} onSuccess={() => { setUploadInfo(null); toast.success("Upload complete"); pollMuxStatus(); }} onError={(e) => toast.error(e?.message || "Upload failed")} style={{"--uploader-background-color":"#111827","--uploader-text-color":"#fff","--uploader-border-color":"#22d3ee","--button-background-color":"#22d3ee","--button-text-color":"#000", "--progress-bar-fill-color":"#22d3ee"}} /></div>}
                {muxMode === "manual" && !safeRecord.mux_playback_id && <div className="mt-4 space-y-3 rounded-xl border border-purple-500/30 bg-zinc-950/60 p-4"><input value={manualPlaybackId} onChange={(e) => setManualPlaybackId(e.target.value)} placeholder="Enter Mux playback ID" className={inputCls} /><div className="flex gap-2"><button type="button" onClick={() => linkAsset(manualPlaybackId)} className={btnPrimary}>Link Asset</button><button type="button" onClick={() => { setMuxMode("upload"); setManualPlaybackId(""); }} className={btnGhost}>Cancel</button></div></div>}
                {muxMode === "library" && !safeRecord.mux_playback_id && <div className="mt-4 space-y-4 rounded-xl border border-pink-500/30 bg-zinc-950/60 p-4"><div className="flex items-center justify-between"><h4 className="font-semibold text-white">Mux Library</h4><button type="button" onClick={() => setMuxMode("upload")} className={btnGhost}>Back</button></div>{libraryLoading ? <div className="py-8 text-center text-zinc-400"><Loader className="animate-spin inline mr-2" size={16} />Loading...</div> : <div className="grid gap-3 md:grid-cols-2">{libraryAssets.map((asset) => <button key={asset.id} type="button" onClick={() => linkAsset(asset.playback_ids?.[0]?.id || asset.playback_id)} className="rounded-lg border border-zinc-700 bg-zinc-900 p-2 text-left hover:border-pink-500"><div className="text-xs text-zinc-400 mb-2">{asset.status}</div><div className="text-sm text-white">{asset.id}</div></button>)}</div>}</div>}
              </div>

              <div className="grid min-w-0 gap-4 lg:grid-cols-2">
              <div className="min-w-0 rounded-xl border border-zinc-800 bg-black/40 p-4">
                <h3 className="text-lg font-bold text-cyan-400 mb-4">Trailer</h3>
                {safeRecord.trailer_mux_playback_id && <MuxPlayer playbackId={safeRecord.trailer_mux_playback_id} streamType="on-demand" className="w-full aspect-video" accent-color="#22d3ee" />}
                <div className="flex flex-wrap gap-2">
                  {!safeRecord.trailer_mux_playback_id && <button type="button" onClick={startTrailerUpload} className={btnGhost}>Upload Trailer</button>}
                  {safeRecord.trailer_mux_playback_id && <button type="button" onClick={removeTrailer} className={btnGhost}><Trash2 size={14} className="inline mr-1" />Remove Trailer</button>}
                  {safeRecord.trailer_mux_upload_id && !safeRecord.trailer_mux_playback_id && <button type="button" onClick={pollTrailerStatus} className={btnGhost}>Refresh trailer status</button>}
                </div>
                {trailerUploadInfo && !safeRecord.trailer_mux_playback_id && <div className="mt-4"><MuxUploader endpoint={trailerUploadInfo.url} onSuccess={() => { setTrailerUploadInfo(null); toast.success("Trailer upload complete"); pollTrailerStatus(); }} onError={(e) => toast.error(e?.message || "Trailer upload failed")} style={{"--uploader-background-color":"#111827","--uploader-text-color":"#fff","--uploader-border-color":"#22d3ee","--button-background-color":"#22d3ee","--button-text-color":"#000", "--progress-bar-fill-color":"#22d3ee"}} /></div>}
              </div>
              {!creating && <TeaserSection videoId={id} />}
              </div>

              <div className="rounded-xl border border-zinc-800 bg-black/40 p-4">
                <h3 className="text-lg font-bold text-cyan-400 mb-4">Video store settings</h3>
                <VideoStoreSettings video={safeRecord} onChange={(updated) => onChange?.({ id, ...updated })} />
              </div>
            </fieldset>
          )}
          {activeTab === "description" && <DescriptionTab record={safeRecord} setField={setField} />}
          {activeTab === "crew" && <CrewTab record={safeRecord} setField={setField} crm={crm} crmLoading={crmLoading} />}
          {activeTab === "cta" && <CtaTab record={safeRecord} setField={setField} allowGlobalEdit={!creating} />}
          {activeTab === "media" && (
            <fieldset disabled={creating} className="min-w-0 space-y-5">
              <section className="rounded-xl border border-zinc-800 bg-black/40 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div><h3 className="font-bold text-white">Master Video</h3><p className="text-xs text-zinc-500 mt-1">{fmtDuration(safeRecord.mux_duration)}{safeRecord.mux_duration ? " duration" : "Duration not available"} · {safeRecord.mux_status || "No playback status"}</p></div>
                  <div className="flex flex-wrap gap-2">
                    {safeRecord.mux_playback_id && <button type="button" className={btnGhost} onClick={() => setShowPreview((v) => !v)}>{showPreview ? "Hide preview" : "Preview"}</button>}
                    <button type="button" className={btnGhost} disabled={masterBusy} onClick={retryPlayback}>{masterBusy ? "Checking…" : "Retry Playback"}</button>
                    <button type="button" className={btnGhost} onClick={() => setTechnicalDetails((v) => !v)}>{technicalDetails ? "Hide technical details" : "View technical details"}</button>
                  </div>
                </div>
                {safeRecord.mux_playback_id ? (
                  showPreview && <MuxPlayer playbackId={safeRecord.mux_playback_id} streamType="on-demand" className="w-full aspect-video mt-4" accent-color="#d946ef" />
                ) : (
                  <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-900/10 p-3 text-sm text-amber-100">
                    <div className="font-semibold">Playback unavailable. The master video is uploaded, but playback could not be prepared.</div>
                    <p className="text-xs text-amber-100/70 mt-1">Possible reasons: upload processing is still running, playback preparation failed, or the Mux asset is no longer available.</p>
                    <div className="flex gap-2 mt-3"><button className={btnGhost} onClick={retryPlayback}>Retry Playback</button><button className={btnGhost} onClick={() => setTechnicalDetails(true)}>View Upload Details</button></div>
                  </div>
                )}
                {technicalDetails && <div className="mt-3 rounded-lg bg-zinc-900 p-3 text-xs text-zinc-400 space-y-1">
                  <div>Upload state: {safeRecord.mux_status || "Unknown"}</div><div>Asset attached: {safeRecord.mux_asset_id ? "Yes" : "No"}</div><div>Last updated: {safeRecord.updated_at ? new Date(safeRecord.updated_at).toLocaleString() : "Unknown"}</div>
                </div>}
              </section>
              <MediaAssetsTab videoId={id} disabled={creating} />
            </fieldset>
          )}
          {activeTab === "photos" && !creating && (
            <div className="space-y-6">
              <section className="space-y-3" data-testid="scene-photos-section">
                <h3 className="font-bold text-white">Scene Photos</h3>
                <ScenePhotos value={safeRecord.scene_photos || []} onChange={(photos) => setField("scene_photos", photos)} videoId={id} />
              </section>
              <LinkedPhotosTab record={safeRecord} setField={setField} />
            </div>
          )}
          {activeTab === "platforms" && (
            <div className="space-y-6">
              <section className="space-y-3" data-testid="external-platform-links-section">
                <h3 className="font-bold text-white">Direct Platform Links</h3>
                {[
                  ["onlyfans_vip", "onlyfans_vip_link", "OnlyFans VIP"],
                  ["onlyfans_free", "onlyfans_free_link", "OnlyFans Free"],
                  ["faphouse", "faphouse_link", "Faphouse"],
                  ["justforfans", "justforfans_link", "JustForFans"],
                ].map(([platform, field, label]) => (
                  <label key={field} className="block text-sm text-zinc-300">
                    {label}
                    <input type="url" className={`${inputCls} mt-1`} value={safeRecord.platform_status?.[platform]?.url ?? safeRecord[field] ?? ""} placeholder="https://"
                      onChange={(e) => setPlatform(platform, { url: e.target.value })} />
                  </label>
                ))}
              </section>
              {!creating && <ExternalLinksTab record={safeRecord} setPlatform={setPlatform} highlightKey={highlightPlatform} onUpdate={handlePlatformUpdate} />}
              {!creating && <section data-testid="comprehensive-platform-matrix-section">
                <PlatformMatrixOverview video={safeRecord} />
              </section>}
            </div>
          )}
          {activeTab === "checklist" && <ChecklistTab record={safeRecord} setField={setField} />}
          {creating && activeTab === "photos" && <p className="text-sm text-zinc-300">Create this video first to attach media. Your draft fields in the other tabs are preserved.</p>}
        </main>

        <footer className="shrink-0 flex flex-wrap items-center justify-between gap-3 border-t border-zinc-800 bg-zinc-950 px-4 sm:px-6 py-3">
          <div className="flex items-center gap-2 text-xs text-zinc-500"><span>{creating ? "Local draft · not saved" : `Video ID · ${String(id).slice(0, 8)}`}</span>{!creating && sync.dirty && <span>· {Object.keys(sync.draft).length} pending field{Object.keys(sync.draft).length === 1 ? "" : "s"}</span>}</div>
          <div className="flex gap-2 ml-auto">
            <button type="button" className={btnGhost} disabled={createBusy} onClick={() => { sync.discard(); onClose(); }}>Cancel</button>
            <button type="button" className={btnPrimary} onClick={saveChanges} disabled={sync.saving || !sync.dirty}><Save size={14} className="inline mr-1" />{creating ? (createBusy ? "Creating…" : "Create Video") : "Save Changes"}</button>
            {!creating && sync.dirty && <button type="button" className={btnGhost} onClick={saveAndClose} disabled={sync.saving}>Save & Close</button>}
          </div>
        </footer>
      </div>
      {sync.conflicts && <ConflictDialog conflicts={sync.conflicts} surface={source} onCancel={doClose} onResolve={sync.resolveConflicts} />}
    </div>
  );
}
